import type { NostrEvent } from '@nostrify/nostrify';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { useState } from 'react';
import {
  getNoteExcerpt,
  getNoteHeading,
  getNoteImage,
  getNoteImageCount,
  getNoteImageSize,
  getNoteVideo,
  formatRelativeTime,
} from '@/lib/noteUtils';
import { cn } from '@/lib/utils';
import { VideoThumbnail } from '@/components/VideoThumbnail';
import { deriveNoteTags } from '@/lib/noteTags';
import { TagBadge } from '@/components/TagBadge';
import { unverifiedPinnedIds } from '@/hooks/usePinnedNotes';
import { Image as ImageIcon, ShieldAlert } from 'lucide-react';

interface NoteCardProps {
  event: NostrEvent;
  /** Compact single-line variant used for dense lists */
  compact?: boolean;
}

/** Return the imeta `thumb` or `image` poster for a given video URL, if present */
function getVideoPoster(event: NostrEvent): string | null {
  for (const tag of event.tags) {
    if (tag[0] !== 'imeta') continue;
    const vals = tag.slice(1);
    let mime: string | null = null;
    let thumb: string | null = null;
    let image: string | null = null;
    for (const v of vals) {
      if (v.startsWith('m ')) mime = v.slice(2).trim();
      if (v.startsWith('thumb ')) thumb = v.slice(6).trim();
      if (v.startsWith('image ')) image = v.slice(6).trim();
    }
    if (mime?.startsWith('video/')) {
      return thumb || image || null;
    }
  }
  return null;
}

/**
 * Image with an onError fallback (hides itself if the URL isn't a valid image).
 *
 * Space is reserved before the picture arrives, so the page doesn't jump as
 * pictures load: the exact shape when the note states its size, otherwise a
 * neutral 4:3 placeholder that gives way to the picture's natural shape once it
 * is in. (Only the second case can still shift, and only slightly.)
 *
 * `alt`: pass "" when the card's own text already describes the record.
 */
function SmartImage({
  src,
  className,
  alt = '',
  size,
}: {
  src: string;
  className: string;
  alt?: string;
  size?: { width: number; height: number } | null;
}) {
  const [hidden, setHidden] = useState(false);
  const [loaded, setLoaded] = useState(false);
  if (hidden) return null;
  return (
    <img
      src={src}
      alt={alt}
      width={size?.width}
      height={size?.height}
      className={cn(className, 'bg-muted', !size && !loaded && 'aspect-[4/3]')}
      loading="lazy"
      decoding="async"
      onLoad={() => setLoaded(true)}
      onError={() => setHidden(true)}
    />
  );
}

/**
 * Media-first "social" note card. Big edge-to-edge media at its natural aspect
 * ratio, with text + tags below. Designed to sit inside a masonry column.
 */
export function NoteCard({ event, compact = false }: NoteCardProps) {
  // Guard against malformed events (missing id) to avoid a hard crash.
  if (!event?.id || typeof event.id !== 'string' || event.id.length !== 64) {
    return null;
  }

  const noteId = nip19.noteEncode(event.id);
  const videoUrl = getNoteVideo(event);
  const videoPoster = videoUrl ? getVideoPoster(event) : null;
  const image = videoUrl ? null : getNoteImage(event);
  const imageSize = getNoteImageSize(event, image);
  // A card shows one picture; this says when there are more behind it.
  const imageCount = image ? getNoteImageCount(event) : 0;
  const relative = formatRelativeTime(event.created_at);
  // Format (image/video) is already visible on the card itself, so cards
  // show the classification that isn't: record type, subject, place, names.
  const allTags = deriveNoteTags(event);
  const derivedTags = allTags.filter(t => t.facet !== 'format').slice(0, 3);
  const hasMedia = Boolean(videoUrl || image);
  const isUnverified = unverifiedPinnedIds.has(event.id);

  // The record's title and the small description under it. A pager intercept IS
  // its message, so it is shown as written and never given a headline.
  const isIntercept = allTags.some(t => t.slug === 'intercept');
  const heading = isIntercept ? { title: null, description: '' } : getNoteHeading(event);

  // ---- Compact variant (dense lists) ----
  if (compact) {
    const excerpt = getNoteExcerpt(event, 120);
    return (
      <Link
        to={`/${noteId}`}
        state={{ fromArchive: true }}
        className="block group border-b border-border pb-3 last:border-0 hover:bg-muted/30 rounded-sm transition-colors p-2 -mx-2"
      >
        <div className="flex gap-3">
          {videoUrl ? (
            <VideoThumbnail
              src={videoUrl}
              poster={videoPoster}
              badgeSize="sm"
              className="w-16 h-16 flex-shrink-0 rounded-sm border border-foreground/25"
            />
          ) : image ? (
            <div className="w-16 h-16 flex-shrink-0">
              <SmartImage
                src={image}
                className="w-16 h-16 object-cover rounded-sm border border-foreground/25"
              />
            </div>
          ) : null}
          <div className="min-w-0">
            <p
              className={cn(
                'text-sm leading-snug text-foreground group-hover:text-primary transition-colors line-clamp-2',
                heading.title && 'font-serif font-bold',
              )}
            >
              {heading.title || excerpt || <span className="text-muted-foreground italic">[media]</span>}
            </p>
            <div className="flex flex-wrap items-center gap-1 mt-1">
              <span className="text-xs text-muted-foreground">{relative}</span>
              {derivedTags.slice(0, 2).map(tag => (
                <TagBadge key={tag.slug} tag={tag} />
              ))}
            </div>
          </div>
        </div>
      </Link>
    );
  }

  // ---- Social variant (default) ----
  const excerpt = getNoteExcerpt(event, 260);

  return (
    <Link
      to={`/${noteId}`}
      state={{ fromArchive: true }}
      className="group block rounded-2xl border border-foreground/25 bg-card overflow-hidden shadow-[0_1px_2px_rgba(40,25,10,0.1),0_8px_16px_-12px_rgba(40,25,10,0.25)] hover:shadow-[0_2px_4px_rgba(40,25,10,0.12),0_14px_24px_-12px_rgba(40,25,10,0.35)] hover:-translate-y-0.5 hover:border-foreground/50 transition-all duration-200 break-inside-avoid"
    >
      {/* Media — natural aspect ratio, edge to edge */}
      {hasMedia && (
        <div className="relative w-full overflow-hidden bg-muted border-b border-foreground/15">
          {videoUrl ? (
            <VideoThumbnail
              src={videoUrl}
              poster={videoPoster}
              badgeSize="lg"
              hoverPreview
              className="w-full min-h-[180px]"
            />
          ) : image ? (
            <SmartImage
              src={image}
              size={imageSize}
              // When the card has text, the text describes the record and the
              // picture needs no second description; with no text, the picture
              // is all there is, so it gets a plain label.
              alt={excerpt ? '' : 'Archive record image'}
              className="w-full h-auto object-cover group-hover:scale-[1.03] transition-transform duration-500 motion-reduce:transition-none"
            />
          ) : null}

          {/* Like the count on a YouTube playlist: the card shows the first
              picture, and this says how many there are in all. */}
          {!videoUrl && imageCount > 1 && (
            <span className="pointer-events-none absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-full bg-black/70 px-2 py-1 font-mono text-[11px] font-semibold leading-none text-white backdrop-blur-sm">
              <ImageIcon aria-hidden="true" className="h-3 w-3" />
              {imageCount}
              <span className="sr-only"> images</span>
            </span>
          )}
        </div>
      )}

      {/* Body */}
      <div className={cn('px-4 py-3', !hasMedia && 'py-5')}>
        {heading.title ? (
          <>
            {/* Like a video's title: bold, and given room (up to three lines). */}
            <h3
              className={cn(
                'font-serif font-bold leading-snug text-foreground line-clamp-3',
                hasMedia ? 'text-base' : 'text-lg md:text-xl',
              )}
            >
              {heading.title}
            </h3>
            {/* The small description, only when the note has more to say. */}
            {heading.description && (
              <p
                className={cn(
                  'mt-1.5 text-sm leading-relaxed text-foreground/75',
                  hasMedia ? 'line-clamp-2' : 'line-clamp-6',
                )}
              >
                {heading.description}
              </p>
            )}
          </>
        ) : excerpt ? (
          <p
            className={cn(
              'leading-relaxed text-foreground whitespace-pre-line',
              hasMedia ? 'text-sm line-clamp-4' : 'text-base md:text-lg font-serif line-clamp-[12]',
            )}
          >
            {excerpt}
          </p>
        ) : (
          !hasMedia && (
            <p className="text-sm text-muted-foreground italic">[no preview available]</p>
          )
        )}

        {/* Footer: tags + time */}
        <div className="flex flex-wrap items-center gap-1.5 mt-3">
          {derivedTags.map(tag => (
            <TagBadge key={tag.slug} tag={tag} />
          ))}
          {isUnverified && (
            <span
              title="Reconstructed copy — the original signed event is not currently available on any reachable relay, so its signature could not be verified."
              className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100/70 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-amber-900 dark:border-amber-800 dark:bg-amber-900/40 dark:text-amber-100"
            >
              <ShieldAlert className="h-3 w-3" />
              Unverified
            </span>
          )}
          <span className="ml-auto text-[11px] uppercase tracking-wider text-muted-foreground font-typewriter shrink-0">
            {relative}
          </span>
        </div>
      </div>
    </Link>
  );
}
